import React, { useEffect, Fragment } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom';
import { useNavigationRef } from './useNavigationRef';
import LoadingComponent from './LoadingComponent';
import { ToastContainer } from 'react-toastify';
import HomePage from '../../features/home/HomePage';
import TopNav from './TopNav';
import { Container } from 'semantic-ui-react';
import PrivateRoute from './PrivateRoute';
import { observer } from 'mobx-react-lite';
import UserProfile from '../../features/profiles/UserProfile';
import SalesDashboard from '../../features/sales/dashboard/SalesDashboard';
import SalesOrderForm from '../../features/sales/forms/SalesOrderForm';
import LoginForm from '../../features/user/LoginForm';
import NotFound from './NotFound';
import ModalContainer from '../common/modals/ModalContainer';
import CustomerDashboard from '../../features/customers/dashboard/CustomerDashboard';
import CreateNewCustomer from '../../features/customers/create/CreateNewCustomer';
import DeleteModal from '../common/modals/DeleteModal';
import PODashboard from '../../features/purchaseorders/PODashboard';
import ScanDashboard from '../../features/scans/ScanDashboard';
import TrackingDashboard from '../../features/trackstatus/TrackingDashboard';
import ProductionKiosk from '../../features/kiosks/ProductionKiosk';
import DeliveryKiosk from '../../features/kiosks/DeliveryKiosk';
import DailyReport from '../../features/dailyreport/DailyReport';
import DailyReportKiosk from '../../features/kiosks/DailyReportKiosk';
import SalesOrderPlanner from '../../features/productionplanning/SalesOrderPlanner';
import Invoice from '../../features/documents/Invoices/Invoice';
import DataSyncDashboard from '../../features/datasyncs/DataSyncDashboard';
import ProductionPlanningPlaceholder from '../../features/productionplanning/ProductionPlanningPlaceholder';
import DailyReportForQB from '../../features/dailyreport/DailyReportForQB';
import { useStore } from '../stores/Store';
import SalesOrderHome from '../../features/salesnew/SalesOrderHome';
import SalesOrderDetail from '../../features/salesnew/details/SalesOrderDetail';
import SalesOrderProcessing from '../../features/charts/SalesOrderProcessing';
import CustomerSalesOrder from '../../features/reports/CustomerSalesOrder';
import PurchaseOrderHome from '../../features/purchaseordersnew/PurchaseOrderHome';
import PurchaseOrderDetail from '../../features/purchaseordersnew/details/PurchaseOrderDetail';
import FireDamperRatesHome from '../../features/rates/FireDamperRatesHome';
import ControlDamperRatesHome from '../../features/rates/ControlDamperRatesHome';
import InventoryDashboard from '../../features/inventory/InventoryDashboard';
import RatesHome from '../../features/rates/RatesHome';
import CostCalculator from '../../features/costing/CostCalculator';
import CostingReport from '../../features/costing/CostingReport';
import CostSalesOrderCalculator from '../../features/costing/CostSalesOrderCalculator';
import PdfWriter from '../../features/pdfcreator/PdfWriter';

const App: React.FC = () => {
    const { commonStore, userStore } = useStore();
    const { appLoaded, setAppLoaded, token } = commonStore;
    const { getUser } = userStore;
    const location = useLocation();
    useNavigationRef(); // Initialize navigation ref for history object

    useEffect(() => {

        if (token) {
            getUser().finally(() => setAppLoaded());
        } else {
            setAppLoaded();
        }
    }, [setAppLoaded, token, getUser])

    if (!appLoaded) return (<LoadingComponent content='Loading ...' />);

    const isHome = location.pathname === '/';

    return (
        <Fragment>
            <DeleteModal />
            <ModalContainer />
            <ToastContainer autoClose={2000} position='bottom-right' />
            {!isHome && <TopNav />}
            {!isHome && <Container style={{ marginTop: '5em' }}>
                <Routes>
                    <Route path='/salesold' element={<PrivateRoute component={SalesDashboard} />} />
                    <Route path='/sales' element={<PrivateRoute component={SalesOrderHome} />} />
                    <Route path='/purchaseorders' element={<PrivateRoute component={PurchaseOrderHome} />} />
                    <Route path='/purchaseorderstabular' element={<PrivateRoute component={PODashboard} />} />

                    <Route path='/costsalesorder' element={<PrivateRoute component={CostSalesOrderCalculator} />} />
                    <Route path='/costsalesorder/:id' element={<PrivateRoute component={CostSalesOrderCalculator} />} />
                    <Route path='/costcalculator' element={<PrivateRoute component={CostCalculator} />} />
                    <Route path='/costingreport' element={<PrivateRoute component={CostingReport} />} />
                    <Route path='/rates' element={<PrivateRoute component={RatesHome} />} />
                    <Route path='/firedamperpricing' element={<PrivateRoute component={FireDamperRatesHome} />} />
                    <Route path='/controldamperpricing' element={<PrivateRoute component={ControlDamperRatesHome} />} />

                    <Route path='/template1' element={<PrivateRoute component={PdfWriter} />} />

                    <Route path='/inventory' element={<PrivateRoute component={InventoryDashboard} />} />
                    <Route path='/salesorder/:id' element={<PrivateRoute component={SalesOrderDetail} />} />
                    <Route path='/purchaseorder/:id' element={<PrivateRoute component={PurchaseOrderDetail} />} />
                    <Route path='/customers' element={<PrivateRoute component={CustomerDashboard} />} />
                    <Route path='/createcustomer' element={<PrivateRoute component={CreateNewCustomer} />} />
                    <Route path='/scan' element={<PrivateRoute component={ScanDashboard} />} />
                    <Route path='/track' element={<PrivateRoute component={TrackingDashboard} />} />
                    <Route path='/createSalesOrder' element={<PrivateRoute component={SalesOrderForm} key={location.key} />} />
                    <Route path='/manage/:id' element={<PrivateRoute component={SalesOrderForm} key={location.key} />} />
                    <Route path='/profile/:username' element={<PrivateRoute component={UserProfile} />} />
                    <Route path='/dailyreport' element={<PrivateRoute component={DailyReport} />} />
                    <Route path='/clientreport' element={<PrivateRoute component={CustomerSalesOrder} />} />
                    <Route path='/dailyreportforquickbooks' element={<PrivateRoute component={DailyReportForQB} />} />
                    <Route path='/productionplanning' element={<PrivateRoute component={ProductionPlanningPlaceholder} />} />
                    <Route path='/salesorderplanner' element={<PrivateRoute component={SalesOrderPlanner} />} />
                    <Route path='/dailyreportkiosk' element={<DailyReportKiosk />} />
                    <Route path='/productionkiosk' element={<ProductionKiosk />} />
                    <Route path='/deliverykiosk' element={<DeliveryKiosk />} />
                    <Route path='/salesorderchart' element={<SalesOrderProcessing />} />
                    <Route path='/sampleinvoice' element={<Invoice />} />
                    <Route path='/datasync' element={<DataSyncDashboard />} />
                    <Route path='/login' element={<LoginForm />} />
                    <Route path='*' element={<NotFound />} />
                </Routes>
            </Container>}
            {isHome && <HomePage />}
        </Fragment>
    )
}

export default observer(App);
